import { useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { BoxCoordinates } from '@/types';
import {
  applyCornerDrag,
  clientToNormalizedPoint,
  isBoxTooSmall,
  normalizeBox,
  translateBox,
  type RegionCorner,
} from '@/lib/regionSelector';

interface RegionSelectorProps {
  imageUrl: string;
  box: BoxCoordinates | null;
  onBoxChange: (box: BoxCoordinates | null) => void;
  disabled?: boolean;
  /**
   * Overlay-only mode: render just the interactive selection layer (no own <img>),
   * meant to be dropped into an existing `relative` container that already shows the
   * image (e.g. the inspector's big preview). `imageUrl` is then ignored.
   */
  overlayOnly?: boolean;
}

const CORNERS: RegionCorner[] = ['tl', 'tr', 'bl', 'br'];

const CORNER_POSITION_CLASS: Record<RegionCorner, string> = {
  tl: '-left-[7px] -top-[7px] cursor-nwse-resize',
  tr: '-right-[7px] -top-[7px] cursor-nesw-resize',
  bl: '-left-[7px] -bottom-[7px] cursor-nesw-resize',
  br: '-right-[7px] -bottom-[7px] cursor-nwse-resize',
};

/**
 * Self-contained region-selection editor for the "Verfeinern" reprompt flow.
 *
 * Renders its own <img> inside a `relative inline-block` wrapper with an `absolute inset-0`
 * overlay, so the selection region aligns pixel-exactly with the rendered image — no
 * `position:fixed` / `getBoundingClientRect`-driven positioning (unlike BoxOverlay.tsx, which
 * stays untouched for the floorplan tool). See src/components/ui/RegionSelector.preview.html
 * for the verified visual/interaction reference this was ported from.
 */
export const RegionSelector: React.FC<RegionSelectorProps> = ({
  imageUrl,
  box,
  onBoxChange,
  disabled = false,
  overlayOnly = false,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  // Tracks the in-progress box across a single drag gesture (draw / resize) so each pointermove
  // step derives from the previous step rather than a stale prop snapshot — mirrors the preview's
  // mutable `box` variable, including the corner-swap behavior on resize.
  const dragBoxRef = useRef<BoxCoordinates | null>(null);

  const overlayRect = useCallback(() => {
    const el = overlayRef.current;
    if (!el) return { left: 0, top: 0, width: 0, height: 0 };
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  }, []);

  const pointFromEvent = useCallback(
    (clientX: number, clientY: number) => clientToNormalizedPoint(clientX, clientY, overlayRect()),
    [overlayRect]
  );

  // ── Draw a new region (pointerdown on the empty overlay) ──────────────────
  const handleOverlayPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      // Only start a fresh draw when the empty overlay itself was hit — clicks on the region body
      // or its handles are handled by their own pointerdown handlers (and stopPropagation there).
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      const start = pointFromEvent(e.clientX, e.clientY);
      const draft: BoxCoordinates = { x1: start.x, y1: start.y, x2: start.x, y2: start.y };
      dragBoxRef.current = draft;
      onBoxChange(draft);

      const onMove = (ev: PointerEvent) => {
        const p = pointFromEvent(ev.clientX, ev.clientY);
        const next: BoxCoordinates = { ...draft, x2: p.x, y2: p.y };
        dragBoxRef.current = next;
        onBoxChange(next);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const finished = dragBoxRef.current;
        dragBoxRef.current = null;
        if (!finished) return;
        // Accidental tap (no meaningful drag) → discard instead of leaving a near-zero-size box.
        onBoxChange(isBoxTooSmall(finished) ? null : normalizeBox(finished));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [disabled, pointFromEvent, onBoxChange]
  );

  // ── Move the region body ───────────────────────────────────────────────────
  const handleRegionPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || !box) return;
      // Corner handles manage their own resize drag; don't also start a move.
      if ((e.target as HTMLElement).dataset.handle) return;
      e.stopPropagation();
      const start = pointFromEvent(e.clientX, e.clientY);
      const original = box;

      const onMove = (ev: PointerEvent) => {
        const p = pointFromEvent(ev.clientX, ev.clientY);
        onBoxChange(translateBox(original, p.x - start.x, p.y - start.y));
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [disabled, box, pointFromEvent, onBoxChange]
  );

  // ── Resize via corner handles ───────────────────────────────────────────────
  const handleCornerPointerDown = useCallback(
    (corner: RegionCorner) => (e: React.PointerEvent<HTMLButtonElement>) => {
      if (disabled || !box) return;
      e.stopPropagation();
      dragBoxRef.current = box;

      const onMove = (ev: PointerEvent) => {
        const p = pointFromEvent(ev.clientX, ev.clientY);
        const current = dragBoxRef.current ?? box;
        const next = applyCornerDrag(current, corner, p);
        dragBoxRef.current = next;
        onBoxChange(next);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const finished = dragBoxRef.current;
        dragBoxRef.current = null;
        // The in-drag value may be intentionally "inverted" (corner-swap, see applyCornerDrag) —
        // clean that up on release so the emitted box is always x1<=x2 / y1<=y2.
        if (finished) onBoxChange(normalizeBox(finished));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [disabled, box, pointFromEvent, onBoxChange]
  );

  const region = box ? normalizeBox(box) : null;

  const overlay = (
    <div
      ref={overlayRef}
      className={cn(
        'absolute inset-0 touch-none',
        disabled ? 'cursor-default' : 'cursor-crosshair'
      )}
      onPointerDown={handleOverlayPointerDown}
    >
      {region && (
        <div
          className="absolute touch-none rounded border-2 border-primary"
          style={{
            left: `${region.x1 * 100}%`,
            top: `${region.y1 * 100}%`,
            width: `${(region.x2 - region.x1) * 100}%`,
            height: `${(region.y2 - region.y1) * 100}%`,
            // Spotlight: dims everything OUTSIDE the region in a single declaration (paired with
            // the wrapper's overflow-hidden) — visually communicates "this stays preserved".
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
            cursor: disabled ? 'default' : 'move',
          }}
          onPointerDown={handleRegionPointerDown}
        >
          <span className="absolute -top-6 left-0 whitespace-nowrap rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-white shadow-sm shadow-primary/40">
            ÄNDERUNGSBEREICH
          </span>
          {!disabled &&
            CORNERS.map((corner) => (
              <button
                key={corner}
                type="button"
                tabIndex={-1}
                aria-label={`Ecke „${corner}“ ziehen zum Skalieren`}
                data-handle={corner}
                onPointerDown={handleCornerPointerDown(corner)}
                className={cn(
                  'absolute h-3.5 w-3.5 rounded-full border-2 border-primary bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)]',
                  'before:absolute before:-inset-[7px] before:content-[""]', // enlarges touch target to ~28px without affecting the visual 14px dot
                  CORNER_POSITION_CLASS[corner]
                )}
              />
            ))}
        </div>
      )}
    </div>
  );

  // Overlay-only: caller supplies the `relative` + image container (e.g. the inspector's
  // big preview). We just layer the interactive selection on top.
  if (overlayOnly) return overlay;

  return (
    <div className="relative inline-block w-full overflow-hidden rounded-lg leading-none">
      <img
        src={imageUrl}
        alt="Aktuelles Ergebnis"
        className="block max-h-96 w-full select-none object-cover"
        draggable={false}
      />
      {overlay}
    </div>
  );
};
